import Layout from 'components/layout';

import React, { type FC, type PropsWithChildren, type SyntheticEvent } from 'react';

import { css } from '@emotion/react';
import { useSiteMetadata, withImage, useSmartlingStrings } from '@lucid/hooks';
import { isDefined } from '@lucid/utils';
import { graphql, type HeadFC } from 'gatsby';
import {
  Box,
  Container,
  Heading,
  Image,
  Text,
  LucidHead,
  AdditionalResources,
  Link,
  reactHTMLParser,
} from '@lucid/components';
import kebabCase from 'lodash/kebabCase';

import type { Lucid } from 'types/types';

const contentsCss = (theme: Lucid.Theme.Brand) =>
  css({
    display: 'inline-block',
    textAlign: 'left',
    paddingRight: '1.875rem',
    paddingLeft: '1.875rem',
    margin: 0,
    [theme.breakpoints.bp1220.min]: {
      paddingRight: theme.space.s48,
      paddingLeft: 0,
    },
  });

const summaryCss = (theme: Lucid.Theme.Brand) =>
  css({
    marginBottom: theme.space.s40,
    p: {
      fontSize: theme.fontSizes.fs16,
      lineHeight: theme.lineHeights.lh1_5,
      margin: `${theme.space.s4}px 0`,
    },
    strong: {
      fontSize: theme.fontSizes.fs16,
      lineHeight: theme.lineHeights.lh1,
      fontWeight: theme.fontWeights.fw500,
      letterSpacing: '0.05rem',
    },
  });

const CaseStudy: FC<PropsWithChildren<Lucid.Context.PageProps>> = ({ data, pageContext }) => {
  const node = data.directus.node;
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Case_Study_Translations;

  const { siteName } = useSiteMetadata();
  const { shortCode, urlLangPrefix, localeId, shouldHideSsr, pageType, nid, metatags, product } = pageContext;
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_nav_contact_sales',
      'mktg_nav_watch_a_demo',
      'mktg_ct_cs_key_benefits',
      'mktg_ct_cs_prefooter_title_product',
      'mktg_ct_cs_additional_resources',
      'mktg_ct_cs_key_benefits',
      'mktg_ct_cs_category_1',
      'mktg_ct_cs_category_2',
      'mktg_ct_cs_category_3',
    ],
    shortCode,
  });
  let sidebarRef: HTMLElement | null = null;
  const arLinks = translatedData?.additional_resources?.map(({ links_id }) => links_id).filter(isDefined);

  const imageOnLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const sidebarBottom = sidebarRef!.offsetTop + sidebarRef!.offsetHeight;
    const image = e.target as HTMLImageElement;
    const imgTop = image.offsetTop;

    if (imgTop > sidebarBottom && image.naturalWidth >= 1200) {
      const parent = image.parentNode as HTMLElement;
      parent.classList.add('extend');
    }
  };

  const ctaPath = data.directus.nodeReference?.translations?.[0]?.path?.url || '/contact/contact-sales';
  const { nodeTranslations } = node;
  // const backButton = data.drupal.nodeReference;
  // const backButtonPath = backButton?.entityUrl ? `${backButton.entityUrl.path}${location.search || ''}` : null;
  const backButtonPath = '/case-studies';

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <Box
        css={theme => ({
          background: `linear-gradient(90deg, ${theme.colors.coolGrey[0]} 50%, ${theme.colors.white} 50%)`,
        })}
      >
        <Container>
          <Box
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto',
              [theme.breakpoints.bp768.min]: {
                flexDirection: 'row',
              },
            })}
          >
            <Box
              css={theme => ({
                background: theme.colors.coolGrey[0],
                [theme.breakpoints.bp768.min]: {
                  flex: '0 1 40%',
                  maxWidth: '405px',
                },
              })}
            >
              <Box css={theme => [contentsCss(theme)]}>
                {!node.is_private && backButtonPath && (
                  <Box
                    css={theme => ({
                      display: 'block',
                      marginBottom: theme.space.s32,
                      [theme.breakpoints.bp768.min]: {
                        marginBottom: theme.space.s48,
                      },
                    })}
                  >
                    <Image
                      css={theme => ({
                        marginTop: theme.space.s48,
                        maxHeight: '200px',
                        [theme.breakpoints.bp768.min]: {
                          display: 'none',
                        },
                      })}
                      imageAlt=""
                      imageSrc={withImage(node?.logo)}
                    />
                  </Box>
                )}
                <Heading
                  as="h1"
                  css={theme => ({
                    display: 'none',
                    [theme.breakpoints.bp768.min]: {
                      display: 'block',
                    },
                  })}
                  variant="hero2XL700"
                >
                  {node.company_name}
                </Heading>
                <Text variant="paragraphM400">{translatedData?.hero_text}</Text>
                <Box
                  css={theme => ({
                    margin: `${theme.space.s32}px 0`,
                    display: 'grid',
                    gridTemplateColumns: 'max-content',
                    gap: theme.space.s16,
                    '& > a': {
                      margin: 0,
                    },
                  })}
                >
                  <Link displayAsButton href={ctaPath}>
                    {withSmartling('mktg_nav_contact_sales')}
                  </Link>
                  <Link displayAsButton href="https://play.goconsensus.com/uecb39571" variant="secondary">
                    {withSmartling('mktg_nav_watch_a_demo')}
                  </Link>
                </Box>
              </Box>
              <Box
                css={theme => ({
                  borderBottom: `1px solid ${theme.colors.coolGrey[20]}`,
                  width: '100%',
                  position: 'relative',
                  right: 0,
                  display: 'inline-block',
                  [theme.breakpoints.bp768.min]: {
                    maxWidth: '464px',
                  },
                })}
              />
              <Box
                ref={ref => {
                  sidebarRef = ref;
                }}
                css={theme => contentsCss(theme)}
              >
                <Heading
                  as="h2"
                  css={theme => ({
                    flex: '0 1 100%',
                    color: theme.colors.coolGrey[100],
                    marginBottom: theme.space.s32,
                    marginTop: theme.space.s64,
                  })}
                  variant="headingXL"
                >
                  {translatedData?.key_benefits_heading || withSmartling('mktg_ct_cs_key_benefits')}
                </Heading>
                <dl>
                  {translatedData?.key_benefits.map(({ name, text }: { name: string; text: string }, index: number) => (
                    <Box key={`${kebabCase(name) || 'case-study-key-benefits'}-${index}`}>
                      <dt
                        css={theme => ({
                          fontSize: theme.fontSizes.fs16,
                          lineHeight: theme.lineHeights.lh1_5,
                          fontWeight: theme.fontWeights.fw500,
                          letterSpacing: '0.05rem',
                          marginBottom: theme.space.s8,
                        })}
                      >
                        {name}
                      </dt>
                      <dd
                        css={theme => ({
                          marginLeft: 0,
                          fontSize: theme.fontSizes.fs16,
                          lineHeight: theme.lineHeights.lh1_5,
                          fontWeight: theme.fontWeights.fw400,
                          marginBottom: theme.space.s40,
                        })}
                      >
                        {text}
                      </dd>
                    </Box>
                  ))}
                </dl>
                <hr
                  css={theme => ({
                    border: `0.5px solid ${theme.colors.coolGrey[10]}`,
                    height: 0,
                    margin: `${theme.space.s48}px 0`,
                  })}
                />
                <Box css={theme => summaryCss(theme)}>
                  {node.industries && node.industries.length > 0 && (
                    <p>
                      <strong>
                        {withSmartling('mktg_ct_cs_category_1')}
                        :&nbsp;
                      </strong>
                      {(node.industries as Lucid.Directus.Generated.Case_Study_Case_Study_Industry[]).map(
                        ({ case_study_industry_id }, i) =>
                          `${case_study_industry_id?.name}${i < node.industries!.length - 1 ? ', ' : ''}`,
                      )}
                    </p>
                  )}
                  {node.size && (
                    <p>
                      <strong>
                        {withSmartling('mktg_ct_cs_category_2')}
                        :&nbsp;
                      </strong>
                      {node.size.name}
                    </p>
                  )}
                  {node.roles && node.roles.length > 0 && (
                    <p>
                      <strong>
                        {withSmartling('mktg_ct_cs_category_3')}
                        :&nbsp;
                      </strong>
                      {(node.roles as Lucid.Directus.Generated.Case_Study_Case_Study_Role[]).map(
                        ({ case_study_role_id }, i) =>
                          `${case_study_role_id!.display_name}${i < node.roles!.length - 1 ? ', ' : ''}`,
                      )}
                    </p>
                  )}
                </Box>
                {translatedData?.summary && (
                  <Box
                    css={theme => summaryCss(theme)}
                    dangerouslySetInnerHTML={{
                      __html: translatedData.summary,
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box
              css={theme => ({
                zIndex: theme.zIndices[2],
                background: theme.colors.white,
                position: 'relative',
                paddingRight: '1.875rem',
                paddingLeft: '1.875rem',
                paddingBottom: theme.space.s32,
                [theme.breakpoints.bp768.min]: {
                  flex: '1 1 60%',
                  maxWidth: '810px',
                  paddingLeft: theme.space.s80,
                },
                '.headerImage': {
                  display: 'none',
                  margin: '48px auto',
                  padding: '0 72px',
                  [theme.breakpoints.bp768.min]: {
                    display: 'block',
                  },
                },
                '.extend': {
                  position: 'relative',
                  width: '100%',
                  margin: `${theme.space.s48}px 0`,
                  display: 'block',
                  [theme.breakpoints.bp1440.min]: {
                    width: '1200px',
                    right: '74%',
                  },
                  img: {
                    width: 'auto',
                    maxWidth: '100%',
                    margin: 0,
                    padding: 0,
                  },
                },
              })}
            >
              {node.logo && node.logo.filename_disk ? (
                <Image
                  className="headerImage"
                  css={theme => ({
                    marginBottom: theme.space.s16,
                    maxHeight: '200px',
                    maxWidth: '100%',
                    height: 'auto',
                  })}
                  imageAlt={`${node.company_name} logo` || ''}
                  imageSrc={withImage(node.logo, { width: 541 })}
                  loading="lazy"
                />
              ) : (
                <Heading
                  as="h2"
                  css={theme => ({
                    marginTop: theme.space.s64,
                  })}
                  variant="heading2XL"
                >
                  {translatedData?.heading}
                </Heading>
              )}
              <Box css={theme => theme.shared.htmlParserStyles}>
                {translatedData?.html_content &&
                  reactHTMLParser(translatedData.html_content, {
                    replaceOptions: { onLoad: imageOnLoad },
                  })}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {arLinks && arLinks.length > 0 && (
        <AdditionalResources
          blurb={translatedData?.additional_resources_heading}
          blurbText={withSmartling('mktg_ct_cs_prefooter_title_product', {
            product: siteName,
          })}
          contactSalesText={withSmartling('mktg_nav_contact_sales')}
          ctaPath={ctaPath!}
          resources={arLinks}
          sectionHeading={withSmartling('mktg_ct_cs_additional_resources')}
        />
      )}
    </Layout>
  );
};

export default CaseStudy;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query CaseStudyQuery($nid: ID!, $shortCode: String!) {
    directus {
      nodeReference: contact_form_by_id(id: "4") {
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          path {
            url
          }
        }
      }
      node: case_study_by_id(id: $nid) {
        nodeTranslations: translations {
          is_published
          languages_code {
            short_code
          }
          path {
            url
          }
        }
        company_name
        logo {
          ...mediaQuery
        }
        hero_image {
          ...mediaQuery
        }
        size {
          name
        }
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          heading
          summary
          html_content
          hero_text
          key_benefits_heading
          key_benefits
          additional_resources_heading
          additional_resources {
            links_id {
              beacon_id
              final_url
              default_link_text
            }
          }
          additional_resources_link_set {
            links_id {
              beacon_id
              final_url
              default_link_text
            }
          }
        }
      }
    }
  }
`;
